/* ============================================================
 * 📅 DATE CHIP SITES — every remaining From / To pair, one chip (owner 2026-10-31)
 * ------------------------------------------------------------
 * The owner's system-wide rule: every date-RANGE filter is the shared
 * DashRangePicker ("📅 Date": one click = that day, a later second click = the
 * range, an earlier second click resets the start), LAST in its period row.
 * Single-date FORM fields (deal / meeting / EOI dates, off-day and vacation
 * forms) stay native inputs.
 *
 * The picker hands back (a, b) as "yyyy-mm-dd" strings, b = "" for one day,
 * ("", "") for Clear. Each page below already had its own state, written by its
 * own two <input type="date">. These adapters are the ONLY translation between
 * the picker and that state, so the page keeps writing exactly the fields it
 * wrote before (same names, same types, same conversion) and its fetch, export,
 * counts and deep links read them unchanged. They are pure and at module scope
 * so verify-mobile-filter-chrome.js §DATE can RUN them.
 *
 * TWO SERVER CONVENTIONS, found in the diagnosis and kept as they are:
 *   - INCLUSIVE `to` (Reports › Meetings, Campaign Strength, Attendance): one
 *     day is from = to = that day.
 *   - EXCLUSIVE `to` (Reports' shared bar — reportsParseRange() falls back to
 *     This Month when from >= to; Assets › History — performedAt $lte the To
 *     day's midnight): the old To input named the first day NOT included, so
 *     a calendar day picked as the end is written as the midnight AFTER it.
 *     Same field, same type, same conversion as the input used; only the day
 *     named is the one the calendar shows as included.
 * ============================================================ */

var DAY_MS = 86400000;
// "yyyy-mm-dd" + n days, UTC arithmetic (no DST drift on a date-only string).
export function ymdAddDays(ymd, n) {
  var t = Date.parse(ymd + "T00:00:00.000Z");
  if (!isFinite(t)) return "";
  return new Date(t + n * DAY_MS).toISOString().slice(0, 10);
}
// A picker value: one day reads back as { from: d, to: "" }.
function pv(from, to) { return { from: from || "", to: (to && to !== from) ? to : "" }; }

/* ── Attendance › Team (AttendancePage). State: dateMode "single" | "range",
 * singleDate, rangeFrom, rangeTo (inclusive "yyyy-mm-dd"). The Single Day /
 * Date Range toggle is the chip itself now: one day = single mode, a range =
 * range mode. Clear = back to the page's own default, today in single mode. */
export var attendanceDateChip = {
  value: function (s) { return s.dateMode === "range" ? pv(s.rangeFrom, s.rangeTo) : pv(s.singleDate, ""); },
  active: function (s) { return s.dateMode === "range" || s.singleDate !== s.todayIso; },
  pick: function (a, b, set) {
    if (!a) { set.setDateMode("single"); set.setSingleDate(set.todayIso); return; }
    if (!b || b === a) { set.setDateMode("single"); set.setSingleDate(a); return; }
    set.setDateMode("range"); set.setRangeFrom(a); set.setRangeTo(b);
  }
};

/* ── Reports, the shared bar (Overview / Pipeline / Agents / Campaigns › ROI).
 * State: filters { preset, from, to } in epoch ms. The From input wrote
 * new Date("yyyy-mm-dd").getTime() (UTC midnight) through setCustomFrom; the
 * To input the same through setCustomTo; both set preset "custom". `to` is
 * EXCLUSIVE on the server. Clear = the page's default preset, This Month. */
export var reportsDateChip = {
  value: function (f) {
    if (!f || f.preset !== "custom" || !isFinite(f.from) || !isFinite(f.to)) return pv("", "");
    var a = new Date(f.from).toISOString().slice(0, 10), b = new Date(f.to - DAY_MS).toISOString().slice(0, 10);
    return pv(a, b < a ? a : b);
  },
  active: function (f) { return !!f && f.preset === "custom"; },
  pick: function (a, b, set) {
    if (!a) { set.setPreset("thisMonth"); return; }
    set.setCustomFrom(new Date(a).getTime());
    set.setCustomTo(new Date(ymdAddDays(b || a, 1)).getTime());
  }
};

/* ── Reports › Meetings (ReportsMeetingsBody). State: rangeFrom, rangeTo
 * ("yyyy-mm-dd" | ""), INCLUSIVE (meetingsReportRange). The range applies only
 * when both are set (customOn), so one day writes both. Clear = clearRange. */
export var meetingsReportDateChip = {
  value: function (s) { return (s.rangeFrom && s.rangeTo) ? pv(s.rangeFrom, s.rangeTo) : pv("", ""); },
  active: function (s) { return !!(s.rangeFrom && s.rangeTo); },
  pick: function (a, b, set) {
    if (!a) { set.setRangeFrom(""); set.setRangeTo(""); return; }
    set.setRangeFrom(a); set.setRangeTo(b || a);
  }
};

/* ── Reports › Campaigns › Strength (ReportsCampaignStrength). State: from, to
 * ("yyyy-mm-dd", always set, INCLUSIVE), written through setRange(a, b), which
 * also moves the year. The chip is lit only when the dates are no preset's
 * range (selOf === "custom"); a preset chip already says which it is.
 * Clear = this month, the page's own default. */
export var campaignStrengthDateChip = {
  value: function (s) { return s.selOf === "custom" ? pv(s.from, s.to) : pv("", ""); },
  active: function (s) { return s.selOf === "custom"; },
  pick: function (a, b, set) {
    if (!a) { set.setRange(set.thisMonth[0], set.thisMonth[1]); return; }
    set.setRange(a, b || a);
  }
};

/* ── Assets › Reports › History (AssetTrackerPage). State: historyFilters
 * { from, to, action } ("yyyy-mm-dd" | ""), sent as-is; the server reads `to`
 * as performedAt $lte its midnight, i.e. EXCLUSIVE of the day named. Clear
 * blanks the dates and keeps the Action filter. */
export var assetsHistoryDateChip = {
  value: function (h) {
    if (!h || !h.from) return pv("", "");
    var b = h.to ? ymdAddDays(h.to, -1) : "";
    return pv(h.from, b < h.from ? h.from : b);
  },
  active: function (h) { return !!(h && h.from); },
  pick: function (a, b, h, setHistoryFilters) {
    if (!a) { setHistoryFilters(Object.assign({}, h, { from: "", to: "" })); return; }
    setHistoryFilters(Object.assign({}, h, { from: a, to: ymdAddDays(b || a, 1) }));
  }
};

/* ── THE REGISTRY. Every date-range picker site in src/, and every date-input
 * PAIR deliberately left native. verify-mobile-filter-chrome.js §DATE parses
 * every <input type="date"> out of src/ and fails when a component holds two
 * or more of them unless it is listed in DATE_INPUT_PAIR_EXCLUSIONS, and fails
 * when a registered site no longer mounts its chip. */
export var DATE_CHIP_SITES = [
  { page: "Dashboard",                 component: "DashboardPage",          file: "src/App.js" },
  { page: "Leads + Daily Request",     component: "DateFilterBar",          file: "src/App.js" },
  { page: "Meetings",                  component: "MeetingsPage",           file: "src/App.js" },
  { page: "Deals",                     component: "DealsPage",              file: "src/App.js" },
  { page: "Attendance › Team",         component: "AttendancePage",         file: "src/App.js" },
  { page: "Reports (shared bar)",      component: "ReportsPage",            file: "src/App.js" },
  { page: "Reports › Meetings",        component: "ReportsMeetingsBody",    file: "src/App.js" },
  { page: "Reports › Campaign Strength", component: "ReportsCampaignStrength", file: "src/pages/ReportsCampaignStrength.js" },
  { page: "Assets › History",          component: "AssetTrackerPage",       file: "src/App.js" }
];
// Form fields, not filters: each one creates or edits a record, and its
// dates are FIELDS of that record (a stage's contract + payment dates, a
// member's joined + left), never a window over a list. Keyed by component,
// then by the bound value of each input (the `value={…}` source text, or a
// cField key), so a NEW From / To pair added to one of these components is
// still caught: only the inputs named here are excused.
export var DATE_INPUT_PAIR_EXCLUSIONS = {
  LeadForm: { why: "EOI / deal / reservation / down-payment / contract dates of the lead form",
    inputs: ['form.eoiDate||""', 'form.dealDate||""', 'form.reservationDate||""', 'form.downPaymentDate||""', 'form.contractionDate||""'] },
  EOIPage: { why: "the Convert-to-Deal form's deal / reservation / down-payment / contract dates",
    inputs: ["dealDate", "reservationDate", "downPaymentDate", "contractionDate"] },
  DealsPage: { why: "the payment-stages form's contract / payment-1 / payment-2 dates",
    inputs: ["stagesForm.contractDate", "stagesForm.payment1Date", "stagesForm.payment2Date"] },
  CompanyOffDaysPage: { why: "the Add off-day form (From + optional To) — an off-day picker",
    inputs: ["newDateFrom", "newDateTo"] },
  UsersPage: { why: "a user's starting date / Saturday-pattern start (edit + add forms)",
    inputs: ['editModal.startingDate||""', 'editModal.saturdayPatternStartDate||""', 'nU.startingDate||""'] },
  TargetPeriodsManager: { why: "a team member's joined / left dates",
    inputs: ["e.joinedAt", "e.leftAt", 'f.joinedAt||""', 'f.leftAt||""'] },
  SettingsPage: { why: "the Schedule-vacation form (start + end) — an off-period form",
    inputs: ["vacStart", "vacEnd"] },
  CommissionEditCollectionModal: { why: "received / payout / new-payment dates of a collection",
    inputs: ["r.receivedDate", "payoutDate", "np.date", "bnp.date"] },
  AddExpenseModal: { why: "the date of each expense row",
    inputs: ["r0.date", "r.date"] }
};
